/**
 * Drizzle query builders + query-key factory de la capa de ORGANIZACIÓN de la
 * academia (org_settings / campus / room). Consumo local-first sobre PowerSync
 * (stream `academy_org`). La escritura pasa por el write-path (`db/sync.rs`),
 * que aplica authz por capacidad (org_rbac) — el cliente nunca es la autoridad.
 */

import {
  and,
  asc,
  campus,
  eq,
  isNull,
  org_settings,
  type PowerSyncDrizzleDB,
  room,
} from "@space/powersync";

/** Claves de caché de la capa de organización (TanStack Query). */
export const academyOrgQueryKeys = {
  all: ["academy-org"] as const,
  settings: (orgId: string | undefined) => ["academy-org", "settings", orgId] as const,
  campuses: (orgId: string | undefined) => ["academy-org", "campuses", orgId] as const,
  rooms: (campusId: string | undefined) => ["academy-org", "rooms", campusId] as const,
  roomsByOrg: (orgId: string | undefined) => ["academy-org", "rooms-by-org", orgId] as const,
};

/** Ajustes (singleton) de una organización. id = organization_id. */
export function getOrgSettingsQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(org_settings).where(eq(org_settings.id, orgId)).limit(1);
}

/** Sedes activas (no borradas) de una organización. */
export function getCampusesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(campus)
    .where(and(eq(campus.organization_id, orgId), isNull(campus.deleted_at)))
    .orderBy(asc(campus.name));
}

/** Aulas activas de una sede. */
export function getRoomsByCampusQuery(db: PowerSyncDrizzleDB, campusId: string) {
  return db
    .select()
    .from(room)
    .where(and(eq(room.campus_id, campusId), isNull(room.deleted_at)))
    .orderBy(asc(room.name));
}

/** Aulas activas de toda la organización. */
export function getRoomsByOrgQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(room)
    .where(and(eq(room.organization_id, orgId), isNull(room.deleted_at)))
    .orderBy(asc(room.name));
}
